import {
  booleanAttribute,
  Directive,
  effect,
  Signal,
  inject,
  input,
  linkedSignal,
  contentChild,
} from '@angular/core';
import { TableConstructUIService } from '../services/table-construct.service';
import { TableResizeUIService } from '../services/table-resize.service';
import { TableCellDefUIDirective } from './table-cell-def.directive';
import { TableHeaderCellDefUIDirective } from './table-header-cell-def.directive';

@Directive({ selector: '[fr8botUiTableColumnDef]' })
export class TableColumnDefUIDirective<DataType> {
  private tableService = inject<TableConstructUIService<DataType>>(TableConstructUIService);
  private resizeService = inject(TableResizeUIService);

  readonly fr8botUiTableColumnDef = input.required<string>();
  readonly fr8botUiTableColumnDefRowName = input<string>();
  /**
   * A row-menu column: a fixed `MENU_COLUMN_WIDTH`, content pushed to the right edge, never
   * resized and never stretched — the last non-menu column takes the free space instead.
   */
  readonly fr8botUiTableColumnDefMenu = input(false, { transform: booleanAttribute });

  private readonly cell = contentChild(TableCellDefUIDirective);
  private readonly headerCell = contentChild(TableHeaderCellDefUIDirective);

  // An empty value keeps the previous name, as the setters these replace did.
  private _rowName = linkedSignal<string | undefined, string | null>({
    source: this.fr8botUiTableColumnDefRowName,
    computation: (rowName, previous) => rowName || previous?.value || null,
  });
  private _columnName = linkedSignal<string, string | null>({
    source: this.fr8botUiTableColumnDef,
    computation: (columnName, previous) => columnName || previous?.value || null,
  });
  public rowName: Signal<string | null> = this._rowName;
  public name: Signal<string | null> = this._columnName;
  public isMenu: Signal<boolean> = this.fr8botUiTableColumnDefMenu;

  constructor() {
    effect(() => {
      const columnName = this._columnName();

      if (columnName && !this._rowName()) {
        this.resizeService.setMenuColumn(columnName, this.isMenu());
      }
    });

    effect(() => {
      const columnName = this._columnName();
      if (columnName) {
        this.tableService.registerColumnTemplate(columnName);
      }
    });

    effect(() => {
      const columnName = this._columnName();
      const cell = this.cell();

      if (columnName && cell) {
        this.tableService.addCell(columnName, cell as TableCellDefUIDirective<DataType>);
      }
    });

    effect(() => {
      const columnName = this._columnName();
      const headerCell = this.headerCell();

      if (columnName && headerCell) {
        const rowName = this._rowName();
        this.tableService.addHeaderCell(`${rowName ? rowName + '_' : ''}${columnName}`, headerCell);
      }
    });
  }
}
